
import {
    Box, Button, Collapse, Flex, Heading, Text, useToast, useDisclosure,
    Img, Link
} from '@chakra-ui/react'
import React, { useEffect, useRef, useState } from 'react'
import ProductLayout from './ProductLayout'
import { getProductData, getBundleData, getAddOnData } from '../../data/products'
import useProductStore from '../../zustand/useProductStore'
import useDataStore from '../../zustand/useDataStore'
import CustomModal from '../Modal/CustomModal'
import CustomEPCModal from '../Modal/CustomEPCModal'
import CustomExternalPhotosModal from '../Modal/CustomPhotoModal'
import arrowDown from '../../assets/images/arrow-down.svg'
import whatsappIcon from '../../assets/images/whatsapp.png'

const usePrevious = (value) => {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
};

const Products = ({ handleNextStep }) => {

    const { products, bundles, addOns } = useDataStore();
    const [isAddonSectionVisible, setIsAddonSectionVisible] = useState(false);
    const [productData, setProductData] = useState([...products]);
    const [productBundleData, setProductBundleData] = useState([...bundles]);
    const [addOnData, setAddOnData] = useState([...addOns]);
    const { isOpen: isModalOpen, onOpen: onOpenModal, onClose: onCloseModal } = useDisclosure();
    const { isOpen: isEPCModalOpen, onOpen: onOpenEPCModal, onClose: onCloseEPCModal } = useDisclosure();
    const { isOpen: isPhotoModalOpen, onOpen: onOpenPhotoModal, onClose: onClosePhotoModal } = useDisclosure();

    const {
        selectedServices,
        setSelectedServices,
        selectedBundle,
        selectedServicesTypes,
        setSelectedServicesTypes,
        setSelectedBundle,
        selectedAddOn,
        setSelectedAddOn,
        setServicePrice,
        discountedPrice,
        setDiscountedPrice,
        price,
        setPrice,
        setProductService,
        setProductBundle,
        setProductAddOn,
        setPropertyDetails,
        propertyDetails,
        properties
    } = useProductStore();

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsAddonSectionVisible(entry.isIntersecting);
            },
            { threshold: 0.1 } // Adjust the threshold as needed
        );

        const elem = document.getElementById('help-desc');
        if (elem) {
            observer.observe(elem);
        }

        return () => {
            if (elem) {
                observer.unobserve(elem);
            }
        };
    }, []);

    useEffect(() => {
        async function fetchProductData() {
            const productData = await getProductData();

            setProductData(productData);
        }
        async function fetchBundleData() {
            const productBundleData = await getBundleData();

            setProductBundleData(productBundleData);
        }

        async function fetchAddOnData() {
            const addOnData = await getAddOnData();

            setAddOnData(addOnData);
        }

        fetchProductData();
        fetchBundleData();
        fetchAddOnData();
    }, []);

    const handlePrice = (serviceSelected, price) => {

        setServicePrice(prevState => {
            const clonedState = { ...prevState };

            if (clonedState[serviceSelected]?.every((val, idx) => val === price[idx])) {

                delete clonedState[serviceSelected];
            } else {
                clonedState[serviceSelected] = price;

            }
            return clonedState;
        });
    }

    const formatter = Intl.NumberFormat('en-GB', {
        currency: 'GBP',
        style: 'currency',
        minimumFractionDigits: 2
    })

    const isProperty = properties.some(property => property.id === propertyDetails.id);
    const propertyIndex = properties.findIndex(property => property.id === propertyDetails.id);

    const productDataHeading = (properties.length > 1 && isProperty)
        ?
        `Please select services required (For Property ${propertyIndex + 1})`
        : ((properties.length > 1 && !isProperty)) ?
            `Please select services required (For Property ${properties.length + 1})`
            :
            'Please select services required *'


    const handleClick = (serviceIndex) => {
        // Check if the service is already selected
        const isSelected = selectedServices.includes(serviceIndex);


        // If the service is selected, deselect it
        if (isSelected) {
            setSelectedServices(prevState => prevState.filter(index => index !== serviceIndex));
            // Deselect the button associated with the service
            setSelectedServicesTypes(prevState => {
                const clonedState = { ...prevState };
                delete clonedState[serviceIndex];
                return clonedState;
            });

            setSelectedAddOn(prevSelectedAddOn =>

                prevSelectedAddOn.filter(addOnId => {
                    const addOn = addOnData.find(addon => addon.id === addOnId);
                    return addOn?.productID !== serviceIndex;
                })
            );
        } else {
            setSelectedServices(prevState => [...prevState, serviceIndex]);

            setSelectedServicesTypes(prevState => {
                const defaultButtonData = productData.find(data => data.id === serviceIndex)?.button?.[1];
                const clonedState = { ...prevState };
                clonedState[serviceIndex] = defaultButtonData?.id ?? 0;
                return clonedState;
            });
            // Assuming default button index is 0
            const defaultButtonData = productData.find(data => data.id === serviceIndex)?.button?.[0];
            if (defaultButtonData) {
                handlePrice(defaultButtonData.heading, [defaultButtonData.discountedPrice ?? 0, defaultButtonData.originalPrice ?? 0]);
                handleImageChange(defaultButtonData.image);
                handleDescriptionChange(defaultButtonData.description);
                setSelectedButtonServiceID(serviceIndex);
            }
        }
        let matchedProduct = productData.filter(
            (product) =>
                product.heading === "EPC" &&
                serviceIndex === product.id &&
                selectedServices.findIndex(e => e === serviceIndex) <= -1
        );

        if (matchedProduct.length > 0) {
            onOpenEPCModal();
        } else {
            onCloseEPCModal();
        }

        // Select the best bundle based on selected services
        let selectedServiceIDs = isSelected ?
            selectedServices.filter(index => index !== serviceIndex) :
            [...selectedServices, serviceIndex];
        let bestBundleId = null;
        let maxServiceCount = 0;
        let minPrice = Number.MAX_SAFE_INTEGER;

        // Find a bundle that has all selected services
        const NoBundleServices = {};
        selectedServiceIDs.forEach(serviceId => {
            const selectedServiceObj = productData.find(product => product.id === serviceId);
            if (selectedServiceObj) {
                const buttonIndex = selectedServiceObj?.button?.findIndex(button => button.id === selectedServicesTypes[serviceId]);
                if (buttonIndex !== -1 && selectedServiceObj.button?.[buttonIndex]) {
                    NoBundleServices[serviceId] = selectedServiceObj.button[buttonIndex].isBundleAllowed;
                } else {
                    NoBundleServices[serviceId] = true; // Default to true if no button or bundle restriction
                }
            } else {
                NoBundleServices[serviceId] = true; // Default to true if service is not found
            }
        });

        if (Object.values(NoBundleServices).filter(e => !!e).length < 1) {
            setSelectedBundle([]);
            return;
        }

        selectedServiceIDs = selectedServiceIDs.filter(e => NoBundleServices[e]);

        const exactMatchBundle = productBundleData.find(bundle => {
            return bundle.productID.length === selectedServiceIDs.length &&
                bundle.productID.every(id => selectedServiceIDs.includes(id))
        });
        if (exactMatchBundle) {
            bestBundleId = exactMatchBundle.id;
        } else if (!exactMatchBundle && selectedServiceIDs.length > 1) {
            // Find a bundle with the maximum number of services
            const selectedServiceIDsSet = new Set(selectedServiceIDs);
            productBundleData
                .filter(data => selectedServiceIDs.length >= data.productID.length)
                .filter(bundle => bundle.productID.every(id => selectedServiceIDsSet.has(id)))
                .forEach(bundle => {
                    const bundleServiceCount = bundle.productID.filter(id => selectedServiceIDs.includes(id)).length;
                    if (bundleServiceCount > maxServiceCount) {
                        bestBundleId = bundle.id;
                        maxServiceCount = bundleServiceCount;
                        minPrice = bundle.discountedPrice;
                    } else if (bundleServiceCount === maxServiceCount && bundle.discountedPrice < minPrice) {
                        bestBundleId = bundle.id;
                        minPrice = bundle.discountedPrice;
                    }
                });
        }

        setSelectedBundle(bestBundleId ? [bestBundleId] : []);
    };

    const handleButtonClick = (e, serviceIndex, buttonIndex, data) => {
        e.stopPropagation();
        const isSelected = selectedServices.includes(serviceIndex);
        const currentButtonIndex = selectedServicesTypes[serviceIndex];



        if (isSelected && currentButtonIndex === data?.id) {
            // Deselect the service and button
            setSelectedServices(prevState => prevState.filter(index => index !== serviceIndex));
            setSelectedServicesTypes(prevState => {
                const clonedState = { ...prevState };
                delete clonedState[serviceIndex];
                return clonedState;
            });

            // Deselect the add-ons related to this service
            setSelectedAddOn(prevSelectedAddOn =>
                prevSelectedAddOn.filter(addOnId => {
                    const addOn = addOnData.find(addon => addon.id === addOnId);
                    return addOn.productID !== serviceIndex;
                })
            );
        } else {
            // Select the service and the clicked button
            if (!isSelected) {
                setSelectedServices(prevState => [...prevState, serviceIndex]);
            }
            setSelectedServicesTypes(prevState => {
                const clonedState = { ...prevState };
                clonedState[serviceIndex] = data?.id ?? buttonIndex;
                return clonedState;
            });
            let matchedProduct = productData.filter(
                (product) =>
                    product.heading === "Property Photography" &&
                    serviceIndex === product.id &&
                    buttonIndex === 0
            );

            if (matchedProduct.length > 0) {
                onOpenPhotoModal();
            } else {
                onClosePhotoModal();
            }

            const defaultButtonData = productData.find(data => data.id === serviceIndex)?.button?.[buttonIndex];
            if (defaultButtonData) {
                handlePrice(defaultButtonData.heading, [defaultButtonData.discountedPrice ?? 0, defaultButtonData.originalPrice ?? 0]);
                setSelectedButtonServiceID(serviceIndex);
                handleImageChange(defaultButtonData.image);
                handleDescriptionChange(defaultButtonData.description);
            }
        }



        // Bundle selection logic
        const selectedServiceObj = productData.find(product => product.id === serviceIndex);
        const NoBundleServices = {}
        Array.from([...selectedServices, serviceIndex]).forEach(e => {
            const temp = { ...selectedServicesTypes };
            temp[serviceIndex] = data?.id ?? buttonIndex
            const buttonId = temp[e];
            const buttonData = productData.find(data => data.id === e)?.button;
            const selectedButton = buttonData?.find(b => b.id === buttonId)
            NoBundleServices[e] = selectedButton?.isBundleAllowed != null ? !!selectedButton?.isBundleAllowed : true;
        });

        if (selectedServiceObj) {
            // Select the best bundle based on selected services
            let updatedSelectedServices = isSelected && currentButtonIndex === data?.id
                ? selectedServices.filter(index => index !== serviceIndex)
                : [...selectedServices.filter(index => index !== serviceIndex), serviceIndex];

            updatedSelectedServices = updatedSelectedServices.filter(e => NoBundleServices[e])

            let bestBundleId = null;
            let maxServiceCount = 0;
            let minPrice = Number.MAX_SAFE_INTEGER;

            const exactMatchBundle = productBundleData.find(bundle =>
                bundle.productID.length === updatedSelectedServices.length &&
                bundle.productID.every(id => updatedSelectedServices.includes(id))
            );

            if (exactMatchBundle) {
                bestBundleId = exactMatchBundle.id;
            } else if (updatedSelectedServices.length > 1) {
                const updatedSelectedServiceSet = new Set(updatedSelectedServices);
                productBundleData
                    .filter(bundle => updatedSelectedServices.length >= bundle.productID.length)
                    .filter(bundle => bundle.productID.every(id => updatedSelectedServiceSet.has(id)))
                    .forEach(bundle => {
                        const bundleServiceCount = bundle.productID.filter(id => updatedSelectedServices.includes(id)).length;
                        if (bundleServiceCount > maxServiceCount) {
                            bestBundleId = bundle.id;
                            maxServiceCount = bundleServiceCount;
                            minPrice = bundle.discountedPrice;
                        } else if (bundleServiceCount === maxServiceCount && bundle.discountedPrice < minPrice) {
                            bestBundleId = bundle.id;
                            minPrice = bundle.discountedPrice;
                        }
                    });
            }

            setSelectedBundle(bestBundleId ? [bestBundleId] : []);
        } else {
            setSelectedBundle([]);
        }
    };

    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedDescription, setSelectedDescription] = useState(null);
    const [selectedButtonServiceID, setSelectedButtonServiceID] = useState(null);

    // To handle image chnage on product having options or button
    const handleImageChange = (image) => {
        setSelectedImage(image);
    };

    const handleDescriptionChange = (description) => {
        setSelectedDescription(description);
    };

    const handleBundleClick = (bundleId) => {
        const bundle = productBundleData.find(b => b.id === bundleId);

        if (!bundle) return;
        const isSelected = selectedBundle.includes(bundleId);
        const hasDuplicate = bundle.productID.some(serviceId => selectedServices.includes(serviceId));

        if (bundle.heading.includes('EPC')) {
            onOpenEPCModal();
        }

        if (isSelected) {
            // Deselect the bundle
            setSelectedBundle(prevSelectedBundle => prevSelectedBundle.filter(id => id !== bundleId));

            // Deselect the services associated with this bundle
            setSelectedServices(prevSelectedServices =>
                prevSelectedServices.filter(serviceId => !bundle.productID.includes(serviceId))
            );

            setSelectedAddOn(prevSelectedAddOn =>
                prevSelectedAddOn.filter(addOnId => {
                    const addOn = addOnData.find(addon => addon.id === addOnId);
                    return !bundle.productID.includes(addOn.productID);
                }))

            // Clear button selections for deselected services
            setSelectedServicesTypes(prevSelectedTypes => {
                const clonedState = { ...prevSelectedTypes };
                bundle.productID.forEach(serviceIndex => {
                    delete clonedState[serviceIndex];
                });
                return clonedState;
            });

            // Clear the price and image for deselected services
            bundle.productID.forEach(serviceIndex => {
                const prod = productData.find(data => data.id === serviceIndex);
                const defaultButtonData = prod?.button?.find(button => button.isBundleAllowed) ?? prod?.button?.[0];
                if (defaultButtonData) {
                    handlePrice(defaultButtonData.heading, [0, 0]);
                }
            });

            return;
        }

        if (hasDuplicate) {
            // If there are duplicate services, deselect all bundles and select only the clicked bundle
            setSelectedBundle([bundleId]);
            setSelectedServices(bundle.productID);
            setSelectedServicesTypes({});

            if (bundleId === '681e0ddb60bb2fcc890f13e7') {
                setSelectedAddOn([]);
            } else {
                setSelectedAddOn(prevSelectedAddOn => {
                    return prevSelectedAddOn.filter(addOnId => {
                        const addOnObj = addOnData.find(addon => addon.id === addOnId);
                        if (!addOnObj) return false;

                        const productIDs = Array.isArray(addOnObj.productID)
                            ? addOnObj.productID
                            : [addOnObj.productID];

                        const hasValidService = productIDs.some(serviceId => bundle.productID.includes(serviceId));
                        return hasValidService;
                    });
                });
            }

            bundle.productID.forEach(serviceIndex => {
                const prod = productData.find(data => data.id === serviceIndex);
                const defaultButtonData = prod?.button?.[1] ?? prod?.button?.[0];
                setSelectedServicesTypes(prevSelectedTypes => {
                    const clonedState = { ...prevSelectedTypes };
                    clonedState[serviceIndex] = prod?.button?.[1]?.id ?? prod?.button?.[0]?.id ?? 0;
                    return clonedState;
                });                

                // Assuming default button index is 0
                if (defaultButtonData) {
                    handlePrice(defaultButtonData.heading, [defaultButtonData.discountedPrice ?? 0, defaultButtonData.originalPrice ?? 0]);
                    handleImageChange(defaultButtonData.image);
                    handleDescriptionChange(defaultButtonData.description);
                    setSelectedButtonServiceID(serviceIndex);
                }
            });
            return;
        }

        // Select the new bundle
        setSelectedBundle(prevSelectedBundle => [...prevSelectedBundle, bundleId]);

        // Select the services associated with this bundle
        setSelectedServices(prevSelectedServices => [...prevSelectedServices, ...bundle.productID.filter(serviceId => !prevSelectedServices.includes(serviceId))]);

        // Set button selections for the services
        bundle.productID.forEach(serviceIndex => {
            const prod = productData.find(data => data.id === serviceIndex);
            const defaultButtonData = prod?.button?.[1] ?? prod?.button?.[0];
            setSelectedServicesTypes(prevSelectedTypes => {
                const clonedState = { ...prevSelectedTypes };
                clonedState[serviceIndex] = defaultButtonData?.id ?? prod?.button?.[0]?.id ?? 0;
                return clonedState;
            });
            // Assuming default button index is 0
            if (defaultButtonData) {
                handlePrice(defaultButtonData.heading, [defaultButtonData.discountedPrice ?? 0, defaultButtonData.originalPrice ?? 0]);
                handleImageChange(defaultButtonData.image);
                handleDescriptionChange(defaultButtonData.description);
                setSelectedButtonServiceID(serviceIndex);
            }
        });
    };

    const productBundleDataHeading = 'Or select services in a package'
    const handleAddOnClick = (serviceIndex) => {
        setSelectedAddOn((prevState) => {
            if (prevState.includes(serviceIndex)) {
                return prevState.filter((index) => index !== serviceIndex);
            } else {
                return [...prevState, serviceIndex];
            }
        });
    };

    const addOnDataHeading = 'Additional services'
    // Addons Logic Ends Here

    const calculateTotalPrice = () => {

        let totalPrice = 0;
        let savedPrice = 0;
    
        const selectedBundles = selectedBundle.map(bundleId =>
            productBundleData.find(b => b.id === bundleId)
        ).filter(Boolean);
    
        const servicesInBundles = selectedBundles.flatMap(bundle => bundle.productID);
    
        const servicesNotInBundles = selectedServices
            .filter(serviceId => !servicesInBundles.includes(serviceId))
            .map(serviceId => {
                const service = productData.find(p => p.id === serviceId);
                const selectedType = selectedServicesTypes[serviceId];
                const selectedButton = service?.button?.find(b => b.id === selectedType);
                return {
                    ...service,
                    price: selectedButton?.originalPrice ?? service?.originalPrice ?? 0
                };
            });
    
        servicesNotInBundles.forEach(service => {
            totalPrice += service.price;
            savedPrice += service.price;
        });
        let selectedBundlePrice = []
        selectedBundles.forEach(bundle => {
            if (bundle.heading === "3D Tour + VT Photos + Floor Plan") {
                // Спеціальна логіка для цього конкретного бандла
                const bundlePrice = bundle.discountedPrice ?? 0;
                totalPrice += Math.round(bundlePrice);
                savedPrice += bundle.originalPrice ?? bundlePrice;
                selectedBundlePrice.push({
                    id: bundle.id,
                    price:Math.round(bundlePrice)
                })
                // якщо є оригінальна — берем її, інакше ту ж саму
            } else {
                const bundleServices = bundle.productID.map(serviceId => {
                    const service = productData.find(p => p.id === serviceId);
                    const selectedType = selectedServicesTypes[serviceId];
                    const selectedButton = service?.button?.find(b => b.id === selectedType);
                    return {
                        price: selectedButton?.originalPrice ?? service?.originalPrice ?? 0,
                        addonPrice: selectedButton?.addonPrice ?? 0
                    };
                });
                bundleServices.sort((a, b) => b.price - a.price);
                const fullPrice = bundleServices[0]?.price ?? 0;
                const discountPrices = bundleServices.slice(1).map(s => s.price * 0.4);
                const addons = bundleServices.map(s => s.addonPrice ?? 0);
                const bundlePrice = fullPrice + discountPrices.reduce((a, b) => a + b, 0) + addons.reduce((a, b) => a + b, 0);
                selectedBundlePrice.push({
                    id: bundle.id,
                    price:Math.round(bundlePrice)
                })
                totalPrice += Math.round(bundlePrice);

                savedPrice += bundleServices.reduce((sum, s) => sum + s.price, 0) + addons.reduce((a, b) => a + b, 0);
            }


        });

        const selectedAddOns = selectedAddOn.map(addOnId => {
            return addOnData.find(item => item.id === addOnId);
        });
    
        selectedAddOns.forEach(addOn => {
            if (addOn) {
                totalPrice += addOn.originalPrice;
                savedPrice += addOn.originalPrice;
            }
        });
    
        setDiscountedPrice(totalPrice);
        setPrice(savedPrice);
        setProductService(servicesNotInBundles);
        setProductBundle(selectedBundle);
        setProductAddOn(selectedAddOn);
        setPropertyDetails({
            ...propertyDetails,
            selectedServices,
            selectedAddOn,
            selectedBundle,
            selectedServicesTypes,
            selectedServicesObj: servicesNotInBundles,
            selectedBundleObj: selectedBundles,
            selectedBundlePrice: selectedBundlePrice,
            selectedAddOnObj: selectedAddOns,
            price: totalPrice,
        });
    
        return {
            totalPrice,
        };
    };    

    const prevSelectedServicesTypes = usePrevious(selectedServicesTypes);
    const prevSelectedServices = usePrevious(selectedServices);

    useEffect(() => {
        if (prevSelectedServicesTypes && !areServicesChanged()) {
            resetAddons(prevSelectedServicesTypes);
        }
    }, [selectedServicesTypes]);

    const areServicesChanged = () => {
        return selectedServices.length !== prevSelectedServices?.length ||
               selectedServices.some((service, index) => service !== prevSelectedServices?.[index]);
    };

    useEffect(() => {
        calculateTotalPrice();
    }, [selectedAddOn, selectedBundle, selectedServices]);

    const resetAddons = (prevSelectedServicesTypes) => {
        const hasServiceTypeChanged = Object.keys(selectedServicesTypes).some((key) => {
            return selectedServicesTypes[key] !== prevSelectedServicesTypes[key];
        });
    
        if (hasServiceTypeChanged) {
            setSelectedAddOn([]);
        } else {
            const validAddOns = addOnData.filter(
                (addon) =>
                    selectedServices.includes(addon.productID) &&
                    (addon.buttonId?.length > 0
                        ? addon.buttonId.includes(selectedServicesTypes[addon.productID])
                        : true)
            ).map((addon) => addon.id);
    
            const updatedAddOn = selectedAddOn.filter((addOnId) => validAddOns.includes(addOnId));
    
            if (updatedAddOn.length !== selectedAddOn.length) {
                setSelectedAddOn(updatedAddOn);
            }
        }
    };

    const filteredAddOnData = addOnData.filter(addon => {

        const baseCondition = selectedServices.includes(addon.productID) &&
            (addon.buttonId?.length > 0 ? addon.buttonId.includes(selectedServicesTypes[addon.productID]) : true);
        // Шукаємо чи обрано 3D Tour Bundle
        const is3DTourBundleSelected = selectedBundle.some(bundleId => {
            const bundle = productBundleData.find(b => b.id === bundleId);
            return bundle && bundle.heading === "3D Tour + VT Photos + Floor Plan";
        });

        // Якщо є активний 3D Tour Bundle, то підміняємо аддон
        if (is3DTourBundleSelected) {
            if (addon.heading === "Add furniture to floor plan" || addon.heading === "Add Schematic Floor Plan") {
                return false; // Не показуємо цей аддон
            }
            if (addon.heading === "Declutter the property") {
                return true; // Додаємо цей аддон
            }
        }
        // Якщо 3D Tour не обрано — стандартна фільтрація
        return baseCondition;
    });

    const toast = useToast()
    const handleContinue = () => {
        if (selectedServices.length === 0) {
            toast({
                title: 'Please select at least 1 service',
                variant: 'left-accent',
                status: 'error',
                duration: 2000,
                position: 'bottom-right'
            });
        } else {
            const selectedService = productData.filter(e => selectedServices.includes(e.id)).find(e => e.heading === "Property Photography");
            if (selectedService && selectedService.button && selectedService.button.find(e => e.id === selectedServicesTypes[selectedService.id])?.heading === "Budget Option") {
                // setModalOpen(true);
                // onOpen();
                onOpenModal();
            } else {
                handleNextStep();
            }
        }
    };

    const handleModalContinue = () => {
        handleNextStep();
    }

    const [isArrowVisible, setIsArrowVisible] = useState(true); 
    const addonSectionRef = useRef(null);
    
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 0) {
                setIsArrowVisible(false);
            } else {
                setIsArrowVisible(true);
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const handleArrowClick = (e) => {
        e.preventDefault();
        const yOffset = -160;
        const y = addonSectionRef.current?.getBoundingClientRect().top + window.scrollY + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
    };

    return (
        <Box position={'relative'}>
            {isArrowVisible && !isAddonSectionVisible &&
                <Img 
                    onClick={handleArrowClick} 
                    src={arrowDown} 
                    alt="arrow down" 
                    width={'32px'} 
                    height={'32px'} 
                    position={'fixed'} 
                    bottom={{base:'6em', md:'10em'}}
                    left={'50%'} 
                    transform={'translateX(-50%)'} 
                    zIndex={99990}
                />
            }

            {/* <Flex flexDirection='column' gap={2} alignItems={'center'} mb={8} display={{ base: 'flex', md: 'none' }}>
                <Heading textAlign='center' variant="p3">Working hours are Mon. - Fri. 9 AM to 5 PM</Heading>
                <Heading textAlign='center' variant="p3">The displayed prices are applicable for London within M25 area</Heading>
                <Heading textAlign='center' variant="p3">For services required outside of this region or during non-standard working hours, please feel free to contact us for further assistance</Heading>
            </Flex> */}

            <ProductLayout productData={productData} heading={productDataHeading} paddingTop={3} imageWidth={"38px"} selectedServices={selectedServices} handleClick={handleClick} handleButtonClick={handleButtonClick} selectedServicesTypes={selectedServicesTypes} selectedDescription={selectedDescription} selectedImage={selectedImage} selectedButtonServiceID={selectedButtonServiceID} minH="110px" />
            <ProductLayout productData={productBundleData} heading={productBundleDataHeading} paddingTop={8} paddingBottom={8} imageWidth={"auto"} selectedServices={selectedBundle} handleClick={handleBundleClick} minTextHeight={'30px'} maxH={'140px'} isPackageSection={true}/>
            {
                <Collapse ref={addonSectionRef} in={filteredAddOnData.length > 0} startingHeight={10}>
                    <ProductLayout productData={filteredAddOnData} heading={addOnDataHeading} paddingTop={3} paddingBottom={2} imageWidth={"auto"} selectedServices={selectedAddOn} handleClick={handleAddOnClick} />
                </Collapse>
            }
            {isModalOpen &&
                <CustomModal isOpen={isModalOpen} onClose={onCloseModal} handleContinue={handleModalContinue} />
            }
            {isEPCModalOpen &&
                <CustomEPCModal isOpen={isEPCModalOpen} onClose={onCloseEPCModal} handleContinue={onCloseEPCModal} />
            }
            {isPhotoModalOpen &&
                <CustomExternalPhotosModal isOpen={isPhotoModalOpen} onClose={onOpenPhotoModal} handleContinue={onClosePhotoModal} />
            }
            <Box position={'sticky'} bottom={0} bg={'white'} py={{ base: 1, md: 2 }} zIndex={'50'} key={price + discountedPrice + selectedBundle.length}>
                <Flex flexDirection='column' gap={2} alignItems={'center'} my={4}>
                    <Heading display={{ base: 'none', md: 'block' }} textAlign='center' variant="h2">Your total cost</Heading>
                    <Heading textAlign='center' variant="p1" display={'flex'} gap={2} alignItems={'center'}>
                        <Text color={'green.600'} fontWeight={600} fontSize={'24px'}>{formatter.format(discountedPrice || 0)}</Text>
                        {(selectedBundle.length > 0) && <> instead of {formatter.format(price || 0)}</>}

                        <Text as="span" display={{ base: 'inline', md: 'none' }} color={'black.600'} fontWeight={'600'}>ex. VAT</Text>
                        <Text as="span" display={{ base: 'none', md: 'inline' }} color={'black.600'} fontWeight={'600'}>* excluding VAT</Text>
                    </Heading>
                    <Button variant={"solid-green-btn"} fontWeight={400} onClick={handleContinue}>Continue</Button>
                    <Link
                        href="https://wa.me/442032876610?text=Hi%2C%20I'm%20on%20your%20online%20shop%20and%20have%20a%20question%20about%20a%20booking.%20Can%20you%20help%20me%2C%20please%3F"
                        isExternal
                        position="fixed"
                        right={{ base: '1rem', md: '10rem' }}
                        bottom={{ base: '1.2rem', md: '4rem'} }
                        zIndex="9990"
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        gap={{ base: '0.5', md: '1'}}
                    >
                        <Text display={{ base: 'inline', md: 'none' }} color={'black.600'} fontSize={'12'} fontWeight={'600'}>Need Help?</Text>
                        <Text display={{ base: 'none', md: 'inline' }} color={'black.600'} fontWeight={'600'}>Need Help?</Text>
                        <Img src={whatsappIcon} alt="WhatsApp" boxSize={{ base: '35px', md: '40px' }} />
                    </Link>
                </Flex>

            </Box>

            <Flex id='help-desc' flexDirection='column' gap={2} alignItems={'center'} mb={8}>
                <Heading textAlign='center' variant="p3">Working hours are Mon. - Fri. 9 AM to 5 PM</Heading>
                <Heading textAlign='center' variant="p3">The displayed prices are applicable for London within M25 area</Heading>
                <Heading textAlign='center' variant="p3">For services required outside of this region or during non-standard working hours, please feel free to contact us for further assistance</Heading>
            </Flex>


        </Box>
    )
}

export default Products 